import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// K2 — Vital Signs management + protocol check (dashboard)

// `lockedPatientId` is set when embedded inside PatientProfile — hides the
// picker and uses that patient's ID directly.
const VitalSignsPage = ({ API_BASE, lockedPatientId = null }) => {
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(lockedPatientId || '');
  const [history, setHistory] = useState([]);
  const [check, setCheck] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);

  function emptyForm() {
    return {
      bpSystolic:'', bpDiastolic:'', pulseRate:'', temperatureC:'', spo2:'',
      bloodSugarFasting:'', bloodSugarRandom:'', weightKg:'', heightCm:'', notes:'',
    };
  }

  useEffect(() => {
    axios.get(`${API_BASE}/patients`).then(r => setPatients((r.data || []).slice(0, 500))).catch(() => {});
  }, [API_BASE]);

  const loadPatientData = useCallback(async (pid) => {
    if (!pid) { setHistory([]); setCheck(null); return; }
    setLoading(true);
    try {
      const [h, c] = await Promise.all([
        axios.get(`${API_BASE}/vitals?patientId=${pid}&limit=20`),
        axios.get(`${API_BASE}/vitals/check/${pid}`),
      ]);
      setHistory(h.data || []);
      setCheck(c.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { loadPatientData(selectedPatientId); }, [selectedPatientId, loadPatientData]);

  const save = async () => {
    if (!selectedPatientId) return alert('Select patient first');
    const body = { patientId: selectedPatientId, recordedBy: 'admin' };
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) body[k] = k === 'notes' ? v : Number(v);
    });
    try {
      await axios.post(`${API_BASE}/vitals`, body);
      setForm(emptyForm());
      loadPatientData(selectedPatientId);
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vital record?')) return;
    await axios.delete(`${API_BASE}/vitals/${id}`);
    loadPatientData(selectedPatientId);
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ color: theme.navy, margin: '0 0 12px' }}>🩺 Vital Signs — Protocol & Recording</h2>

      <div style={{ ...cardStyle, marginBottom: 12, background: theme.primaryLight, border: `1px solid ${theme.primary}` }}>
        <div style={{ fontSize: '0.85rem', color: theme.navy, lineHeight: 1.5 }}>
          <b>Why:</b> For diabetic / hypertensive / cardiac / pregnant patients, record BP and sugar before every treatment. System flags stale (&gt;30d old) readings + auto-detects high BP (≥140/90), low BP (≤90/60), high sugar (fasting ≥126 / random ≥200), tachycardia (&gt;100 bpm), fever (≥38°C).
        </div>
      </div>

      {!lockedPatientId && (
        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <label style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>Patient</label>
          <select value={selectedPatientId} onChange={e => setSelectedPatientId(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
            <option value="">— Select patient —</option>
            {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone || p.uhid || '—'})</option>)}
          </select>
        </div>
      )}

      {selectedPatientId && check && (
        <div style={{
          ...cardStyle, marginBottom: 12,
          background: check.isStale ? '#fee2e2' : check.required ? '#fef3c7' : '#dcfce7',
          borderLeft: `4px solid ${check.isStale ? theme.danger : check.required ? theme.warning : theme.success}`,
        }}>
          <div style={{ fontWeight: 700, color: check.isStale ? '#991b1b' : check.required ? '#92400e' : '#166534', marginBottom: 6 }}>
            {check.isStale ? '⚠️ ACTION REQUIRED' : check.required ? '🔶 PROTOCOL APPLIES' : '✅ NO SPECIAL PROTOCOL'}
          </div>
          <div style={{ fontSize: '0.88rem', color: theme.navy, marginBottom: 6 }}>{check.recommendation}</div>
          {check.reasons.length > 0 && (
            <div style={{ fontSize: '0.78rem', color: '#666' }}>Risk factors: {check.reasons.join(', ')}</div>
          )}
          {check.warnings.length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              {check.warnings.map((w, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: theme.danger, fontWeight: 600 }}>⚠️ {w}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedPatientId && (
        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 10px', color: theme.navy }}>Record New Vitals</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
            <Field label="BP Systolic" value={form.bpSystolic} onChange={v => setForm({ ...form, bpSystolic: v })}/>
            <Field label="BP Diastolic" value={form.bpDiastolic} onChange={v => setForm({ ...form, bpDiastolic: v })}/>
            <Field label="Pulse (bpm)" value={form.pulseRate} onChange={v => setForm({ ...form, pulseRate: v })}/>
            <Field label="Temp (°C)" value={form.temperatureC} onChange={v => setForm({ ...form, temperatureC: v })} step="0.1"/>
            <Field label="SpO2 %" value={form.spo2} onChange={v => setForm({ ...form, spo2: v })}/>
            <Field label="Sugar Fasting" value={form.bloodSugarFasting} onChange={v => setForm({ ...form, bloodSugarFasting: v })}/>
            <Field label="Sugar Random" value={form.bloodSugarRandom} onChange={v => setForm({ ...form, bloodSugarRandom: v })}/>
            <Field label="Weight (kg)" value={form.weightKg} onChange={v => setForm({ ...form, weightKg: v })} step="0.1"/>
            <Field label="Height (cm)" value={form.heightCm} onChange={v => setForm({ ...form, heightCm: v })}/>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600 }}>Notes</label>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inputStyle}/>
          </div>
          <button onClick={save} style={{ ...btnStyle, background: theme.primary, color: 'white', marginTop: 10 }}>
            💾 Save Vitals
          </button>
        </div>
      )}

      {selectedPatientId && (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: '#f9fafb', fontWeight: 700, color: theme.navy, borderBottom: '1px solid #e5e7eb' }}>
            History ({history.length})
          </div>
          {loading ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Loading…</div>
           : history.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>No vitals recorded yet.</div>
           : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                <th style={th}>When</th><th style={th}>BP</th><th style={th}>Pulse</th>
                <th style={th}>Temp</th><th style={th}>SpO2</th>
                <th style={th}>Sugar F/R</th><th style={th}>Flags</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {history.map(v => (
                  <tr key={v._id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontSize: '0.78rem', color: '#666' }}>{new Date(v.recordedAt).toLocaleString('en-IN')}</td>
                    <td style={{ ...td, fontWeight: v.isHighBP || v.isLowBP ? 700 : 400, color: v.isHighBP || v.isLowBP ? theme.danger : theme.navy }}>
                      {v.bpSystolic || '—'}/{v.bpDiastolic || '—'}
                    </td>
                    <td style={{ ...td, color: v.hasTachycardia ? theme.danger : theme.navy, fontWeight: v.hasTachycardia ? 700 : 400 }}>{v.pulseRate || '—'}</td>
                    <td style={{ ...td, color: v.hasFever ? theme.danger : theme.navy, fontWeight: v.hasFever ? 700 : 400 }}>{v.temperatureC || '—'}</td>
                    <td style={td}>{v.spo2 || '—'}</td>
                    <td style={{ ...td, fontWeight: v.isHighSugar ? 700 : 400, color: v.isHighSugar ? theme.danger : theme.navy }}>
                      {v.bloodSugarFasting || '—'}/{v.bloodSugarRandom || '—'}
                    </td>
                    <td style={{ ...td, fontSize: '0.72rem' }}>
                      {v.isHighBP && <span style={{ color: theme.danger, fontWeight: 700 }}>↑BP </span>}
                      {v.isLowBP && <span style={{ color: theme.warning, fontWeight: 700 }}>↓BP </span>}
                      {v.isHighSugar && <span style={{ color: theme.danger, fontWeight: 700 }}>↑Sugar </span>}
                      {v.hasTachycardia && <span style={{ color: theme.danger, fontWeight: 700 }}>Tachy </span>}
                      {v.hasFever && <span style={{ color: theme.danger, fontWeight: 700 }}>Fever</span>}
                    </td>
                    <td style={td}>
                      <button onClick={() => remove(v._id)} style={{ padding: '3px 8px', fontSize: '0.7rem', background: theme.danger, color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, step = '1' }) => (
  <div>
    <label style={{ fontSize: '0.7rem', color: theme.textMuted, fontWeight: 600 }}>{label}</label>
    <input type="number" step={step} value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, padding: '6px 8px', fontSize: '0.85rem' }}/>
  </div>
);

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', color: '#555', borderBottom: '1px solid #e5e7eb' };
const td = { padding: '10px 12px', fontSize: '0.85rem', color: '#333' };

export default VitalSignsPage;
